/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import { FileInput, FileUploader } from "@/components/extension/file-upload";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { CarouselProgram } from "@/dashboard/components/layout/Corps";
import { useModal } from "@/dashboard/store/store";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import { autoScrollJoyrideStep } from "@/libs/joyrideAutoScroll";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Joyride, { CallBackProps, STATUS } from "react-joyride";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";

const FormSchema = z.object({
  programPhoto: z.string().optional(),
  programName: z
    .string()
    .min(1, {
      message: "Le nom du programme est obligatoire.",
    })
    .max(120, { message: "120 caractères maximum" }),
  programDescription: z.string().optional(),
});

interface MyData {
  name: string;
  description: string;
}
const Dashboard = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "common",
    useSuspense: true,
  });

  const { user } = useAuth();

  const steps = [
    {
      target: "html",
      content: (
        <div>
          <h2 className="text-xl font-bold mb-4">
            {t("Découvrez votre tableau de bord Verdeo")}
          </h2>
          <p className="text-white/80">
            {t(
              "Un tour rapide pour vous familiariser avec votre tableau de bord et ses fonctionnalités."
            )}
          </p>
        </div>
      ),
      placement: "center",
      disableBeacon: true, // Démarre automatiquement
      spotlightClicks: true, // Désactive l’interaction avec l’arrière-plan
    },
    {
      target: "#sidebar #mes-programmes", // Sidebar
      title: t("Gérez vos programmes et projets"),
      content: (
        <div>
          <h2 className="text-xl font-bold mb-4"></h2>
          <p className="text-white/80">
            {t("Ici, vous pouvez accéder à vos programmes et projets.")}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true, // Démarre automatiquement
      spotlightClicks: true, // Désactive l’interaction avec l’arrière-plan
      before: () => {
        document
          .getElementById("mes-programmes")
          ?.scrollIntoView({ behavior: "smooth", block: "end" });
      },
      disableScrolling: false,
    },
    {
      target: "#sidebar #notification", // Sidebar
      title: t("Notifications"),
      content: (
        <div>
          <p className="text-white/80">
            {t("Appuyez ici pour accéder à vos notifications")}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true, // Démarre automatiquement
      spotlightClicks: true, // Désactive l’interaction avec l’arrière-plan
      before: () => {
        document
          .getElementById("notification")
          ?.scrollIntoView({ behavior: "smooth", block: "end" });
      },
      disableScrolling: false,
    },
    {
      target: "#add-program",
      title: t("Ajoutez un nouveau programme"),
      content: (
        <div className="text-center">
          <p>{t("Cliquez ici pour ajouter un nouveau programme.")}</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      spotlightClicks: true, // Désactive l’interaction avec l’arrière-plan
    },
    {
      target: "#mes-programs",
      title: t("Vos programmes"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Ici, vous trouverez tous vos programmes en cours. Cliquez sur un programme pour en savoir plus."
            )}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
      spotlightClicks: true, // Désactive l’interaction avec l’arrière-plan
    },
    {
      target: "#suivi-financement",
      title: t("Suivi du financement"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Cette barre vous montre l'état des fonds demandés par rapport au total des projets."
            )}
          </p>
        </div>
      ),
      placement: "top",
      disableBeacon: true,
      spotlightClicks: true, // Désactive l’interaction avec l’arrière-plan
    },
    {
      target: "#repartition-fond",
      title: t("Répartition des fonds"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Visualisez la répartition des financements déjà obtenus pour chaque type de projet"
            )}
          </p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      spotlightClicks: true, // Désactive l’interaction avec l’arrière-plan
    },
    {
      target: "#data-reporting",
      title: t("Indicateurs"),
      content: (
        <div className="text-center">
          <p>{t("Vue d'ensemble de vos indicateurs")}</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      spotlightClicks: true, // Désactive l’interaction avec l’arrière-plan
    },
  ];
  const [run, setRun] = useState(false);

  useEffect(() => {
    // Vérifie si l'utilisateur a déjà vu le tutoriel
    const hasSeenTutorial = localStorage.getItem("hasSeenTutorial");
    if (!hasSeenTutorial || user?.email === "demo@verdeo.fr") {
      setRun(true);
    }
  }, [user]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    autoScrollJoyrideStep(data);
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === "skip") {
      setRun(false);
      localStorage.setItem("hasSeenTutorial", "true"); // Enregistre que l'utilisateur a terminé le tutoriel
    }
  };
  const navigate = useNavigate();
  const { handleCompressImage } = useImageCompressor();
  const { postData, loading: loadingSave, response } = usePostData<MyData>();
  const hidePostActions = useHidePostActions();
  const postMultipart = usePostMultipart();
  const [searchParams] = useSearchParams();
  const [uploadPhotoLoading, setUploadPhotoLoading] = useState(false);
  const { setOpenModal, modals } = useModal();
  useEffect(() => {
    if (searchParams.get("openmodal") == "true") {
      setTimeout(() => {
        setOpenModal({ name: "program", value: true });
      }, 500);
    }
  }, []);

  const queryClient = useQueryClient();
  const [nameChar, setNameChar] = useState(0);
  const handlePhotoProgram = async (value) => {
    if (value) {
      setUploadPhotoLoading(true);

      // Compresser l'image avant l'upload
      const compressedBlob = await handleCompressImage(value[0], 500, 300, 0.9);

      // Si l'image est compressée, elle est disponible dans `compressedImageBlob`
      const data = new FormData();
      data.append("type", 8);
      const fileDefaultName = value[0].name;
      if (compressedBlob) {
        for (let i = 0; i < value.length; i++) {
          const renamedFile = new File(
            [compressedBlob], // contenu
            fileDefaultName, // nouveau nom
            { type: compressedBlob.type } // garder le type MIME d'origine
          );
          data.append("files[]", renamedFile);
        }
      } else {
        for (let i = 0; i < value.length; i++) {
          data.append("files[]", value[0]);
        }
      }
      const newMedias = await postMultipart.postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });
      if (
        !Array.isArray(newMedias) &&
        newMedias.success !== undefined &&
        newMedias.success !== null &&
        !newMedias.success
      ) {
        toast({
          title: newMedias.message,
          duration: 1000,
        });
        setUploadPhotoLoading(false);
        return;
      }
      // await saveCover(newMedias[0]);
      setManagerLogo(newMedias[0]);
      setUploadPhotoLoading(false);
      return;
    }
  };
  const [managerLogo, setManagerLogo] = useState(response?.data?.name || null);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      programPhoto: "",
      programName: "",
      programDescription: "",
    },
  });
  async function onSubmit(formData: z.infer<typeof FormSchema>) {
    const data = {
      name: formData.programName,
      description: formData.programDescription || "",
      ...(managerLogo?.id && { cover: managerLogo.id }),
    };

    const result = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/program/save`,
      data,
    });

    form.reset();
    setManagerLogo(null);
    const newProgram = {
      id: result.id as number,
      name: result.name as string,
      description: result.description as string,
      cover: result.cover as string,
      createdAt: result.createdAt as Date,
      projects: result.projects as object,
      status: result.status as number,
    };
    setOpenModal({ name: "program", value: false });
    toast({
      title: t("Programme créé avec succès"),
      duration: 2000,
    });
    queryClient.invalidateQueries({ queryKey: [`programs`] });
    queryClient.invalidateQueries({ queryKey: [`programsInfo`] });
    // setTimeout(function () {
    //   window.location.reload();
    // }, 1000);
  }
  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        callback={handleJoyrideCallback}
        continuous={false}
        scrollToFirstStep={false}
        disableScrolling={true}
        showProgress={true}
        showSkipButton={true}
        spotlightClicks={false}
        locale={{
          back: t("Précédent"),
          close: t("Suivant"),
          last: t("Terminer"),
          next: t("Suivant"),
          skip: t("Passer"),
        }}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "  #383937",
            overlayColor: "rgba(255, 255, 255, 0.8)",
            spotlightShadow: "0 0 0 rgba(0, 0, 0, 0)",
            textColor: "#fff",
            zIndex: 1000,
          },
          buttonBack: {
            backgroundColor: " #669523",
            color: "white",
            borderRadius: "20px",
            marginRight: "20px",
            width: "100px",
            fontSize: "15px",
          },

          buttonNext: {
            backgroundColor: "#669523",
            color: "white",
            borderRadius: "20px",
            width: "80px",
            fontSize: "15px",
          },
        }}
      />
      <h1 className="mb-5 text-3xl font-medium text-foreground ">
        {t("Tableau de bord")}
      </h1>
      <h2 className="text-xl font-semibold  inline-block" id="mes-programs">
        {t("Mes Programmes")}
      </h2>
      <CarouselProgram />

      <div
        className="modal-program mb-4 w-full min-w-52 px-4 md:w-1/2 lg:w-1/3"
        style={{ display: "none" }}
      >
        <div className=" h-[175px] w-full " id="addProgram">
          <Dialog
            open={modals["program"]}
            onOpenChange={(value: boolean) =>
              setOpenModal({ name: "program", value: value })
            }
          >
            <DialogTrigger asChild style={{ display: "none" }}>
              <div className="flex size-full flex-col items-start justify-center">
                <Button
                  variant="outline"
                  className=" flex size-full items-center justify-center rounded-[15px] border"
                  style={{ background: "#EFEFEF" }}
                >
                  <div className="flex w-max  items-center">
                    <svg
                      width="48"
                      height="36"
                      viewBox="0 0 48 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.61257 36C3.34411 36 2.25863 35.5597 1.35609 34.6793C0.452031 33.7973 0 32.7375 0 31.5V4.5C0 3.2625 0.452031 2.20351 1.35609 1.323C2.25863 0.441 3.34411 0 4.61257 0H16.5476C17.1626 0 17.7492 0.1125 18.3073 0.3375C18.8639 0.5625 19.3536 0.881249 19.7765 1.29375L23.0628 4.5H41.5131C42.7815 4.5 43.8679 4.941 44.772 5.823C45.6744 6.70351 46.1257 7.7625 46.1257 9H21.1602L16.5476 4.5H4.61257V31.5L9.16747 16.7062C9.47498 15.7312 10.0423 14.9527 10.8695 14.3707C11.6952 13.7902 12.6077 13.5 13.6071 13.5H43.3581C44.9341 13.5 46.1742 14.109 47.0782 15.327C47.9806 16.5465 48.2205 17.8687 47.7976 19.2938L43.6463 32.7938C43.3389 33.7687 42.7723 34.5472 41.9467 35.1293C41.1195 35.7098 40.2063 36 39.2068 36H4.61257ZM9.45576 31.5H39.2068L43.3581 18H13.6071L9.45576 31.5Z"
                        fill="#D4D4D4"
                      />
                      <rect
                        x="25"
                        y="19"
                        width="3"
                        height="11"
                        rx="1"
                        fill="#D9D9D9"
                      />
                      <rect
                        x="32"
                        y="23"
                        width="3"
                        height="11"
                        rx="1"
                        transform="rotate(90 32 23)"
                        fill="#D9D9D9"
                      />
                    </svg>
                  </div>
                </Button>
                <div className=" mt-4 flex cursor-pointer items-center gap-2 font-semibold text-foreground opacity-70">
                  {t("Ajouter")}
                </div>
              </div>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <DialogHeader>
                    <DialogTitle className="text-2xl">
                      {t("Ajout de programme")}
                    </DialogTitle>
                    <DialogDescription>{t("Remplissez")}</DialogDescription>
                  </DialogHeader>

                  <div className="flex flex-wrap">
                    <div className="basis-1/3 pr-4">
                      <FormLabel className="mb-3 block">
                        {t("Image du programme")}
                      </FormLabel>
                      <FileUploader
                        value={[]}
                        onValueChange={handlePhotoProgram}
                        dropzoneOptions={{
                          multiple: false,
                        }}
                        className={`relative h-full rounded-lg bg-background`}
                      >
                        <FileInput className="border-indigo-foreground size-full border-2 border-dashed">
                          {!uploadPhotoLoading && managerLogo === null && (
                            <div className="flex size-full flex-col items-center justify-center pb-4 pt-3">
                              <svg
                                className="size-8 text-gray-500 dark:text-gray-400"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 20 16"
                              >
                                <path
                                  stroke="currentColor"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                                />
                              </svg>
                              {t("Photo")}
                            </div>
                          )}
                          {!uploadPhotoLoading && managerLogo !== null && (
                            <div className="flex size-full flex-col items-center justify-center">
                              <img
                                src={
                                  import.meta.env.VITE_MEDIA_BASE +
                                  managerLogo.uri
                                }
                                alt=""
                                className="size-full object-cover"
                              />
                            </div>
                          )}
                          {uploadPhotoLoading && (
                            <div
                              className={`w-35 relative mr-2 flex h-24 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-input bg-gray-200`}
                            >
                              <div className="customfileuploaderloader">
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                                <div className="block"></div>
                              </div>
                            </div>
                          )}
                        </FileInput>
                      </FileUploader>
                    </div>
                    <div className="basis-2/3 pl-4">
                      <FormField
                        control={form.control}
                        name="programName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("Nom du programme")}
                              <span className="text-destructive">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                onKeyUp={(e) =>
                                  setNameChar(e.currentTarget.value.length)
                                }
                                placeholder={t("Nom du programme")}
                                {...field}
                              />
                            </FormControl>
                            <div className="flex w-full">
                              <div className="w-3/6">
                                <FormMessage />
                              </div>
                              <FormDescription
                                className={
                                  "w-3/6 text-right" +
                                  (nameChar > 120 ? " text-red-500" : "")
                                }
                              >
                                {nameChar} / 120&nbsp;{t("caractères")}
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="programDescription"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("Description du programme")}
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder={t("Description du programme")}
                                {...field}
                                className="w-full"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    {!hidePostActions && (
                      <Button type="submit" disabled={loadingSave}>
                        {t("Sauvegarder")}
                      </Button>
                    )}
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
